/**
 * Scene 1 — "A Normal Day". Channel-Load + Stress-Heatmap pro Operations-Zone.
 *
 * Zeigt das Tier-1-Aggregate-Use-Case: PTT-Frequenz + akustischer Stress-
 * Score pro Zone, aggregiert k>=5, ueber einen ganzen 24h-Arbeitstag.
 * Time-Slider scrollt durch den Tag, Heatmap aktualisiert live, AMZEL-
 * Narrator zeigt Insights bei den Spikes.
 *
 * Komplett mock-deterministisch — kein Backend-Hit, reproduzierbar im Call.
 * Generische Zonen + Site Alpha — keine Site-Codes.
 */
import { useMemo, useState } from "react";
import { Activity, AlertCircle, BarChart3, Sparkles, Users } from "lucide-react";

import { DEMO_SITE, DEMO_ZONES } from "../demo-constants";
import {
  activityAtMinute,
  formatTimeOfDay,
  generateDayActivity,
  insightForMinute,
} from "./scene1-data";

export function SceneNormalDay() {
  // Generate einmal — deterministisch, kein State-Re-Render
  const samples = useMemo(generateDayActivity, []);

  // Default-Slider auf 14:30 (Peak-Zeit)
  const [minute, setMinute] = useState(14 * 60 + 30);

  const currentActivity = useMemo(
    () => activityAtMinute(samples, minute),
    [samples, minute],
  );
  const insight = useMemo(() => insightForMinute(samples, minute), [samples, minute]);

  // Totals over current snapshot
  const totalPtt = Array.from(currentActivity.values()).reduce(
    (a, b) => a + b.pttRate,
    0,
  );
  const avgStress = Math.round(
    Array.from(currentActivity.values()).reduce((a, b) => a + b.stressScore, 0) /
      Math.max(1, currentActivity.size),
  );

  return (
    <div className="space-y-6">
      {/* Top: Title + Time-Slider */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold tracking-tight">
              {DEMO_SITE.label} · {DEMO_SITE.region}
            </h2>
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              Voice-traffic and acoustic stress aggregated by zone — k≥5 anonymised.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
              Time of day
            </span>
            <span className="rounded-full bg-slate-900 px-3 py-1 font-mono text-sm font-semibold text-white dark:bg-slate-100 dark:text-slate-900">
              {formatTimeOfDay(minute)}
            </span>
          </div>
        </div>

        <div className="mt-5">
          <input
            type="range"
            min={0}
            max={1435}
            step={5}
            value={minute}
            onChange={(e) => setMinute(Number(e.target.value))}
            className="w-full accent-emerald-600"
            aria-label="Scroll through the day"
          />
          <div className="mt-1 flex justify-between text-[10px] font-mono text-slate-400 dark:text-slate-500">
            <span>00:00</span>
            <span>06:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>24:00</span>
          </div>
        </div>
      </div>

      {/* Middle: KPI strip + Insight card */}
      <div className="grid gap-4 lg:grid-cols-4">
        <KpiTile
          icon={<Activity className="size-5" aria-hidden />}
          label="PTT events / 5 min"
          value={`${totalPtt}`}
          tone="info"
        />
        <KpiTile
          icon={<Users className="size-5" aria-hidden />}
          label="Active talkgroups"
          value={`${Array.from(currentActivity.values()).filter((a) => a.pttRate > 5).length}`}
          tone="info"
        />
        <KpiTile
          icon={<BarChart3 className="size-5" aria-hidden />}
          label="Avg stress score"
          value={`${avgStress}`}
          tone={avgStress > 70 ? "warn" : "ok"}
        />
        <KpiTile
          icon={<AlertCircle className="size-5" aria-hidden />}
          label="Alerts"
          value={insight ? "1" : "0"}
          tone={insight ? "warn" : "ok"}
        />
      </div>

      {/* Insight card */}
      {insight && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-500/30 dark:bg-emerald-500/10">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 size-5 shrink-0 text-emerald-700 dark:text-emerald-400" aria-hidden />
            <div className="flex-1">
              <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                AMZEL Insight
              </div>
              <p className="mt-1 text-sm leading-relaxed text-emerald-900 dark:text-emerald-100">
                <strong>{insight.zoneLabel}</strong> zone is at{" "}
                <strong>{insight.pttMultiplier}×</strong> baseline voice activity
                ({insight.pttRate} PTT events in the last 5 minutes).
                Acoustic stress score is{" "}
                <strong>{insight.stressScore}</strong> — {insight.stressScore > 70 ? "elevated, suggest the shift lead check in." : "within normal range."}
              </p>
              <p className="mt-2 text-xs text-emerald-800/80 dark:text-emerald-200/80">
                Edge-aggregated · individual radios anonymised · no transcripts left the site.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Floor plan grid */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
            Zone heatmap
          </h3>
          <Legend />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {DEMO_ZONES.map((zone) => {
            const a = currentActivity.get(zone.id);
            return (
              <ZoneCard
                key={zone.id}
                label={zone.label}
                pttRate={a?.pttRate ?? 0}
                stressScore={a?.stressScore ?? 0}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const KPI_TONE_CLS = {
  ok: "text-emerald-700 dark:text-emerald-400",
  warn: "text-amber-700 dark:text-amber-400",
  info: "text-slate-900 dark:text-slate-100",
} as const;

function KpiTile({
  icon, label, value, tone,
}: Readonly<{
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: "ok" | "warn" | "info";
}>) {
  const toneCls = KPI_TONE_CLS[tone];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        <span className="text-slate-400 dark:text-slate-500">{icon}</span>
        <span>{label}</span>
      </div>
      <div className={`mt-2 text-3xl font-bold tracking-tight tabular-nums ${toneCls}`}>
        {value}
      </div>
    </div>
  );
}

function stressToneCls(score: number): string {
  if (score > 75) return "text-rose-700 dark:text-rose-400";
  if (score > 60) return "text-amber-700 dark:text-amber-400";
  return "text-emerald-700 dark:text-emerald-400";
}

function ZoneCard({
  label, pttRate, stressScore,
}: Readonly<{ label: string; pttRate: number; stressScore: number }>) {
  // Color based on PTT rate (0-50 → opacity 0-1)
  const intensity = Math.min(1, pttRate / 50);
  const stressTone = stressToneCls(stressScore);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      {/* Activity gradient overlay */}
      <div
        className="pointer-events-none absolute inset-0 bg-linear-to-br from-emerald-400/30 via-amber-400/30 to-rose-500/40"
        style={{ opacity: intensity * 0.7 }}
        aria-hidden
      />
      <div className="relative">
        <div className="text-sm font-semibold tracking-tight">{label}</div>
        <div className="mt-3 space-y-2">
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
              PTT / 5 min
            </span>
            <span className="font-mono text-lg font-bold tabular-nums">{pttRate}</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
              Stress
            </span>
            <span className={`font-mono text-lg font-bold tabular-nums ${stressTone}`}>
              {stressScore}
            </span>
          </div>
          {/* Mini activity bar */}
          <div className="mt-2 h-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
            <div
              className="h-full rounded-full bg-linear-to-r from-emerald-500 via-amber-500 to-rose-500"
              style={{ width: `${intensity * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
      <LegendDot color="bg-emerald-500" label="quiet" />
      <LegendDot color="bg-amber-500" label="active" />
      <LegendDot color="bg-rose-500" label="peak" />
    </div>
  );
}

function LegendDot({ color, label }: Readonly<{ color: string; label: string }>) {
  return (
    <div className="flex items-center gap-1">
      <span className={`size-2 rounded-full ${color}`} aria-hidden />
      <span>{label}</span>
    </div>
  );
}
